import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { apiClient } from '@/api/axios';

export type RecordType = 'vaccine' | 'grooming' | 'treatment' | 'document' | 'note' | 'medical';
export type RecordStatus = 'draft' | 'proposed' | 'approved' | 'rejected';

export interface ProposedRecord {
  id: string;
  petId: string;
  petName: string;
  petDID: string;
  professionalId: string;
  professionalName: string;
  professionalType: string;
  professionalBusinessName: string;
  recordType: RecordType;
  status: RecordStatus;
  data: any; // Flexible data structure based on record type
  submittedDate: string;
  reviewedDate?: string;
  rejectionReason?: string;
  ownerComment?: string;
  supportingDocuments?: string[];
}

interface ProposedRecordsContextType {
  proposedRecords: ProposedRecord[];
  loadProposedRecordsForPet: (petId: string) => Promise<void>;
  addProposedRecord: (record: Omit<ProposedRecord, 'id' | 'submittedDate' | 'status'>) => void;
  updateRecordStatus: (recordId: string, status: RecordStatus, comment?: string, rejectionReason?: string) => void;
  getProposedRecordsForPet: (petId: string) => ProposedRecord[];
  getProposedRecordsByProfessional: (professionalId: string) => ProposedRecord[];
  getPendingRecordsForOwner: (ownerId: string) => ProposedRecord[];
  requestEdit: (recordId: string, comment: string) => void;
  approveProposal: (recordId: string, comment?: string) => Promise<void>;
  rejectProposal: (recordId: string, rejectionReason?: string) => Promise<void>;
}

const ProposedRecordsContext = createContext<ProposedRecordsContextType | undefined>(undefined);

export function ProposedRecordsProvider({ children }: { children: ReactNode }) {
  const [proposedRecords, setProposedRecords] = useState<ProposedRecord[]>([]);

  const loadProposedRecordsForPet = useCallback(async (petId: string) => {
    try {
      console.log('[ProposedRecords] Fetching for pet:', petId);
      const res = await apiClient.get(`/pets/${petId}/proposed-records`);
      console.log('[ProposedRecords] API response:', res.data);
      const records = (res.data.proposedRecords || []).map((r: any) => ({
        id: r.id,
        petId: r.petId,
        petName: '',
        petDID: '',
        professionalId: r.professionalId,
        professionalName: r.professionalName,
        professionalType: r.professionalType,
        professionalBusinessName: r.professionalBusinessName || '',
        recordType: r.recordType,
        status: r.status,
        data: r.data,
        submittedDate: r.submittedAt,
        reviewedDate: r.reviewedAt,
        rejectionReason: r.rejectionReason,
        ownerComment: r.ownerComment,
        supportingDocuments: r.supportingDocuments,
      }));
      setProposedRecords((prev) => {
        // Merge: replace records for this pet, keep others
        const otherPets = prev.filter((p) => p.petId !== petId);
        return [...otherPets, ...records];
      });
    } catch (err) {
      console.error('[ProposedRecords] Failed to load:', err);
    }
  }, []);

  const addProposedRecord = (record: Omit<ProposedRecord, 'id' | 'submittedDate' | 'status'>) => {
    const newRecord: ProposedRecord = {
      ...record,
      id: `prop-${Date.now()}`,
      submittedDate: new Date().toISOString(),
      status: 'proposed'
    };
    setProposedRecords((prev) => [...prev, newRecord]);
  };

  const updateRecordStatus = (recordId: string, status: RecordStatus, comment?: string, rejectionReason?: string) => {
    setProposedRecords((prev) => prev.map(record => {
      if (record.id === recordId) {
        return {
          ...record,
          status,
          reviewedDate: new Date().toISOString(),
          ownerComment: comment,
          rejectionReason: rejectionReason
        };
      }
      return record;
    }));
  };

  const requestEdit = (recordId: string, comment: string) => {
    setProposedRecords((prev) => prev.map(record => {
      if (record.id === recordId) {
        return {
          ...record,
          status: 'draft' as RecordStatus,
          ownerComment: comment
        };
      }
      return record;
    }));
  };

  const approveProposal = async (recordId: string, comment?: string) => {
    // Capture petId before mutating so we can refetch.
    const record = proposedRecords.find((r) => r.id === recordId);
    try {
      await apiClient.post(`/proposed-records/${recordId}/approve`, { comment });
      updateRecordStatus(recordId, 'approved', comment);
      // Refetch from server so the status change persists across refreshes
      // and any list UI that filters by status re-syncs.
      if (record?.petId) await loadProposedRecordsForPet(record.petId);
    } catch (err) {
      console.error('[ProposedRecords] approve failed:', err);
      // Re-throw so the UI can surface the error instead of silently
      // pretending the approval worked.
      throw err;
    }
  };

  const rejectProposal = async (recordId: string, rejectionReason?: string) => {
    const record = proposedRecords.find((r) => r.id === recordId);
    try {
      await apiClient.post(`/proposed-records/${recordId}/reject`, { reason: rejectionReason });
      updateRecordStatus(recordId, 'rejected', undefined, rejectionReason);
      if (record?.petId) await loadProposedRecordsForPet(record.petId);
    } catch (err) {
      console.error('[ProposedRecords] reject failed:', err);
      throw err;
    }
  };

  const getProposedRecordsForPet = (petId: string) => {
    return proposedRecords.filter(record => record.petId === petId);
  };

  const getProposedRecordsByProfessional = (professionalId: string) => {
    return proposedRecords.filter(record => record.professionalId === professionalId);
  };

  const getPendingRecordsForOwner = (_ownerId: string) => {
    return proposedRecords.filter(record => record.status === 'proposed');
  };

  return (
    <ProposedRecordsContext.Provider
      value={{
        proposedRecords,
        loadProposedRecordsForPet,
        addProposedRecord,
        updateRecordStatus,
        getProposedRecordsForPet,
        getProposedRecordsByProfessional,
        getPendingRecordsForOwner,
        requestEdit,
        approveProposal,
        rejectProposal
      }}
    >
      {children}
    </ProposedRecordsContext.Provider>
  );
}

export function useProposedRecords() {
  const context = useContext(ProposedRecordsContext);
  if (context === undefined) {
    throw new Error('useProposedRecords must be used within a ProposedRecordsProvider');
  }
  return context;
}
